import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, Clock, MapPin } from "lucide-react";
import siteSettings from "../data/site-settings.json";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: "Home", path: "/" },
    { name: "About Us", path: "/about" },
    { name: "Products", path: "/products" },
    { name: "Contact", path: "/contact" },
  ];

  const categories = [
    { name: "Flour & Baking", path: "/products/flour-baking" },
    { name: "Rice & Grains", path: "/products/rice-grains" },
    { name: "Oils & Ghee", path: "/products/oils-ghee" },
    { name: "Spices & Seasonings", path: "/products/spices-seasonings" },
    { name: "Nuts & Pulses", path: "/products/nuts-pulses" },
    { name: "Dairy & Grocery", path: "/products/dairy-grocery" },
  ];

  return (
    <footer className="bg-deepCharcoal text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center space-x-3 mb-5">
              <img
                src={siteSettings.logo}
                alt={siteSettings.company_name}
                className="h-12 w-12 object-contain rounded-sm"
              />
              <div>
                <p className="font-cormorant text-lg font-bold text-white leading-tight">
                  {siteSettings.company_name}
                </p>
                <p className="text-xs text-premiumGold tracking-[0.2em] uppercase font-outfit font-semibold">
                  {siteSettings.brand}
                </p>
              </div>
            </div>
            <p className="font-outfit text-sm text-white/70 leading-relaxed mb-5">
              Premium wholesale and retail supplier of food commodities in UAE. Quality products, reliable service, competitive pricing.
            </p>
            <a
              href={`https://wa.me/${siteSettings.whatsapp}?text=${encodeURIComponent(
                "Hello AL ZAHRAN FLOUR MILL, I would like information about your products."
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 bg-premiumGold text-deepCharcoal px-4 py-2.5 text-sm font-semibold font-outfit hover:bg-[#B8944A] transition-colors duration-200"
            >
              <span>WhatsApp Order</span>
            </a>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-cormorant text-lg font-semibold text-white mb-5 tracking-wide">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="font-outfit text-sm text-white/70 hover:text-premiumGold transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Product Categories */}
          <div>
            <h4 className="font-cormorant text-lg font-semibold text-white mb-5 tracking-wide">
              Our Products
            </h4>
            <ul className="space-y-3">
              {categories.map((cat) => (
                <li key={cat.path}>
                  <Link
                    to={cat.path}
                    className="font-outfit text-sm text-white/70 hover:text-premiumGold transition-colors duration-200"
                  >
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-cormorant text-lg font-semibold text-white mb-5 tracking-wide">
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <Phone size={15} className="text-premiumGold mt-0.5 flex-shrink-0" />
                <div>
                  <a
                    href={`tel:${siteSettings.phone}`}
                    className="font-outfit text-sm text-white/80 hover:text-premiumGold transition-colors block"
                  >
                    {siteSettings.phone}
                  </a>
                  <a
                    href={`tel:${siteSettings.landline}`}
                    className="font-outfit text-sm text-white/80 hover:text-premiumGold transition-colors block"
                  >
                    {siteSettings.landline}
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Mail size={15} className="text-premiumGold mt-0.5 flex-shrink-0" />
                <a
                  href={`mailto:${siteSettings.email}`}
                  className="font-outfit text-sm text-white/80 hover:text-premiumGold transition-colors break-all"
                >
                  {siteSettings.email}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Clock size={15} className="text-premiumGold mt-0.5 flex-shrink-0" />
                <span className="font-outfit text-sm text-white/80">{siteSettings.hours}</span>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin size={15} className="text-premiumGold mt-0.5 flex-shrink-0" />
                <span className="font-outfit text-sm text-white/80">{siteSettings.address}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-outfit text-xs text-white/50 text-center sm:text-left">
            &copy; {currentYear} {siteSettings.company_name} | {siteSettings.brand}. All rights reserved.
          </p>
          <p className="font-outfit text-xs text-white/40">
            Premium Food Commodity Supplier, Dubai UAE
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
